import type {NotificationDictionary} from "@/components/notifications/NotificationDrawer";
import type {Locale} from "@/i18n-config";
import type {AccountNotification} from "@/utils/notifications/account/types";
import {resolveLocalizedNotificationValue} from "@/utils/notifications/localization";

export type NotificationContent = {
    categoryKey?: string;
    category: string;
    title: string;
    description?: string;
    action?: {
        label: string;
        href: string;
    };
};

export function resolveAccountNotificationContent(
    notification: AccountNotification,
    locale: Locale,
    dictionary: NotificationDictionary,
): NotificationContent {
    switch (notification.type) {
        case "account_verified":
            return {
                category: dictionary.notificationAccountCategory,
                title: dictionary.notificationAccountVerifiedTitle,
                description: dictionary.notificationAccountVerifiedDescription,
            };
        case "export_started":
            return {
                category: dictionary.notificationExportCategory,
                title: dictionary.notificationExportStartedTitle,
                description: dictionary.notificationExportStartedDescription.replace(
                    "{name}",
                    notification.payload.reportName,
                ),
            };
        case "export_ready":
            return {
                category: dictionary.notificationExportCategory,
                title: dictionary.notificationExportReadyTitle,
                description: dictionary.notificationExportReportDescription.replace(
                    "{name}",
                    notification.payload.reportName,
                ),
            };
        case "text":
            return {
                categoryKey: notification.payload.categoryKey,
                category: notification.payload.categoryKey === "feature"
                    ? dictionary.notificationFeatureCategory
                    : resolveLocalizedNotificationValue(
                        notification.payload.category,
                        locale,
                    ),
                title: resolveLocalizedNotificationValue(
                    notification.payload.title,
                    locale,
                ),
                description: notification.payload.description
                    ? resolveLocalizedNotificationValue(
                        notification.payload.description,
                        locale,
                    )
                    : undefined,
                action: notification.payload.action
                    ? {
                        label: resolveLocalizedNotificationValue(
                            notification.payload.action.label,
                            locale,
                        ),
                        href: resolveLocalizedNotificationValue(
                            notification.payload.action.href,
                            locale,
                        ),
                    }
                    : undefined,
            };
        case "release_note":
            return {
                category: [
                    dictionary.notificationReleaseNoteLabel,
                    notification.payload.version,
                ].filter(Boolean).join(" · "),
                title: resolveLocalizedNotificationValue(
                    notification.payload.title,
                    locale,
                ),
                description: notification.payload.description
                    ? resolveLocalizedNotificationValue(
                        notification.payload.description,
                        locale,
                    )
                    : undefined,
                action: {
                    label: dictionary.notificationReleaseNoteAction,
                    href: notification.payload.href,
                },
            };
    }
}
